import React from 'react';

import { PRODUCT_TYPE_E_COUPON } from '~/domains/e-coupon/constants';
import InfiniteSpreadhowcase from '~/domains/mmp/InfiniteSpreadShowcase';
import InfiniteSwipeShowcase from '~/domains/mmp/InfiniteSwipeShowcase';
import ECouponProductCard from '~/domains/products/components/ECouponProductCard';
import ProductCard from '~/domains/products/components/ProductCard';
import ProductGridDisplay from '~/domains/products/components/ProductGridDisplay';
import ProductCarouselDisplay from '~/domains/products/ProductCarouselDisplay';
import ProductListDisplay from '~/domains/products/ProductListDisplay';
import { useShowcases } from '~/domains/showcase/hooks';
import type { OAISchema } from '~/types/openapi';

import EventECouponProductButtonCard from '../EventECouponProductButtonCard';
import EventProductButtonCartCard from '../EventProductButtonCartCard';
import EventProductWideDisplay from '../EventProductWideDisplay';

// https://koreacreditdata.atlassian.net/wiki/spaces/MP/pages/3680011225/MMP
// size 를 API 요청해서 보내도, 서버에서 필터링되는 케이스가 있어서 정확하게 내려오지 않음
// 차라리 API response를 slice 해서 보여주는게 갯수 면에서 정확도가 높음
const defaultSize = (displayType: string) => {
  switch (displayType) {
    case 'grid_2x2':
      return {};
    case 'grid_3x2':
      return { size: 96 }; // 20개보다 더 호출하기 위함
    case 'list_1x3':
      return {};
    case 'carousel_1x10':
      return {};
    case 'wide_1x2':
      return {};
    case 'grid_button_2x2':
      return { size: 96 };
    case 'grid_spread_3x2':
      return { size: 96 };
    case 'grid_spread_2x2':
      return { size: 96 };
    default:
      return {};
  }
};

type ShowcaseProduct = OAISchema['MarketSelectProductResponseV2'];

type Props = {
  showcaseCode: string;
  displayType?: string;
  showcaseParams?: Record<string, unknown>;
};

const EventShowcaseBody = ({
  showcaseCode,
  displayType = 'grid',
  showcaseParams = { ...defaultSize(displayType) },
}: Props) => {
  const { data } = useShowcases(showcaseCode, showcaseParams);

  if (!data || !data.products || !data.products.content) return null;

  const list = data.products.content;

  return (
    <>
      {(() => {
        switch (displayType) {
          case 'grid_2x2':
            return (
              <ProductGridDisplay
                data={list.slice(0, 4)}
                eventOption={{
                  displayType: 'grid_2x2',
                }}
              />
            );
          case 'grid_3x2':
            return (
              <div className="-mx-8">
                <InfiniteSwipeShowcase<ShowcaseProduct>
                  key={showcaseCode}
                  list={list}
                  columnCount={3}
                  rowCount={2}
                  renderToProduct={(product) => (
                    <>
                      {product.productType === PRODUCT_TYPE_E_COUPON ? (
                        <ECouponProductCard
                          {...product}
                          eventOption={{
                            displayType: 'grid_3x2',
                          }}
                          is3x2
                        />
                      ) : (
                        <ProductCard
                          {...product}
                          // 3x2 컴포넌트는 할인율 비노출
                          discountPercentage={undefined}
                          eventOption={{
                            displayType: 'grid_3x2',
                          }}
                          plusMembershipFullBadge
                          is3x2
                        />
                      )}
                    </>
                  )}
                />
              </div>
            );
          case 'list_1x3':
            return (
              <ProductListDisplay
                products={list.slice(0, 3)}
                eventOption={{
                  displayType: 'list_1x3',
                }}
              />
            );
          case 'carousel_1x10':
            return (
              <div className="-mx-20">
                <ProductCarouselDisplay
                  products={list.slice(0, 10)}
                  eventOption={{
                    displayType: 'carousel_1x10',
                  }}
                />
              </div>
            );
          case 'wide_1x2':
            // 이벤트 페이지에서 1개만 노출
            return (
              <EventProductWideDisplay
                products={list.slice(0, 2)}
                eventOption={{
                  displayType: 'wide_1x2',
                }}
              />
            );
          case 'grid_button_2x2':
            return (
              <div className="-mx-8">
                <InfiniteSwipeShowcase<ShowcaseProduct>
                  key={showcaseCode}
                  list={list}
                  columnCount={2}
                  rowCount={2}
                  renderToProduct={(product) => (
                    <>
                      {product.productType === PRODUCT_TYPE_E_COUPON ? (
                        <EventECouponProductButtonCard
                          item={product}
                          eventOption={{
                            displayType: 'grid_button_2x2',
                          }}
                          reverseCouponDiscountedPrice
                        />
                      ) : (
                        <EventProductButtonCartCard
                          item={product}
                          eventOption={{
                            displayType: 'grid_button_2x2',
                          }}
                          reverseCouponDiscountedPrice
                        />
                      )}
                    </>
                  )}
                />
              </div>
            );
          case 'grid_spread_2x2':
            return (
              <div className="-mx-8">
                <InfiniteSpreadhowcase<ShowcaseProduct>
                  key={showcaseCode}
                  list={list}
                  columnCount={2}
                  rowCount={2}
                  renderToProduct={(product) => (
                    <>
                      {product.productType === PRODUCT_TYPE_E_COUPON ? (
                        <ECouponProductCard
                          {...product}
                          eventOption={{
                            displayType: 'grid_spread_2x2',
                          }}
                          reverseCouponDiscountedPrice
                        />
                      ) : (
                        <ProductCard
                          {...product}
                          eventOption={{
                            displayType: 'grid_spread_2x2',
                          }}
                          reverseCouponDiscountedPrice
                          plusMembershipFullBadge
                        />
                      )}
                    </>
                  )}
                />
              </div>
            );
          case 'grid_spread_3x2':
            return (
              <div className="-mx-8">
                <InfiniteSpreadhowcase<ShowcaseProduct>
                  key={showcaseCode}
                  list={list}
                  columnCount={3}
                  rowCount={2}
                  renderToProduct={(product) => (
                    <>
                      {product.productType === PRODUCT_TYPE_E_COUPON ? (
                        <ECouponProductCard
                          {...product}
                          eventOption={{
                            displayType: 'grid_spread_3x2',
                          }}
                          is3x2
                        />
                      ) : (
                        <ProductCard
                          {...product}
                          // 3x2 컴포넌트는 할인율 비노출
                          discountPercentage={undefined}
                          eventOption={{
                            displayType: 'grid_spread_3x2',
                          }}
                          plusMembershipFullBadge
                          is3x2
                        />
                      )}
                    </>
                  )}
                />
              </div>
            );
          default:
            // grid
            return (
              <ProductGridDisplay
                data={list.slice(0, 4)}
                eventOption={{
                  displayType: 'grid_2x2',
                }}
              />
            );
        }
      })()}
    </>
  );
};

export default EventShowcaseBody;
